import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { ApiError } from 'shared/errors/ApiError';
import { useCompanySourceOptionsQuery } from 'shared/hooks/inventory/useCompanySourceOptionsQuery';
import { useReceivePurchaseOrderMapGroupMutation } from 'shared/hooks/receiving/useReceivePurchaseOrderMapGroupMutation';

import { buildPurchaseOrderMapGroupReceivePayload } from './buildPurchaseOrderMapGroupReceivePayload';
import { buildReceivedPrintDataFromApiResponse } from './buildReceivedPrintDataFromApiResponse';
import { buildCompanySourceOptionsFromMaps, buildSuggestedAllocations } from './buildSuggestedAllocations';
import {
    getCaseQtyValidationError,
    getExpirationValidationError,
    resolveExpirationDefaults,
} from './resolveExpirationDefaults';
import { AllocationBreakdown } from './AllocationBreakdown/AllocationBreakdown';
import { PalletBuilderCard } from './PalletBuilder/PalletBuilderCard';
import { palletStagingReducer, sanitizeNumPalletsInput } from './PalletBuilder/palletBuilderUtils';
import { PalletQuickFillCard } from './PalletBuilder/PalletQuickFillCard';
import { PrintAllocatedLabelsModal } from './PrintLabels/PrintAllocatedLabelsModal';
import { ReceiveFooter } from './ReceiveFooter/ReceiveFooter';

function getHeaderText(rowData, poType) {
    if (poType === 'PACKAGING') {
        return `Sku ${rowData.original.masterSku} - ${rowData.original.description} (${rowData.original.type})`;
    }
    return `Sku ${rowData.original.itemMasterSKU} - ${rowData.original.itemMasterName}`;
}

function getPurchaseOrderMapsFromRow(rowData, poType) {
    if (!rowData?.original) {
        return [];
    }

    if (poType === 'PACKAGING') {
        return rowData.original.packagingPurchaseOrderMaps ?? [];
    }

    return rowData.original.purchaseOrderMaps ?? [];
}

function getLineItemKey(rowData) {
    if (!rowData?.original) {
        return null;
    }

    const { packagingPurchaseOrderMapGroupId, purchaseOrderMapGroupId } = rowData.original;

    return purchaseOrderMapGroupId ?? packagingPurchaseOrderMapGroupId ?? null;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getReceiveErrorMessage(error) {
    if (error instanceof ApiError || error instanceof Error) {
        return error.message;
    }

    return 'Failed to receive purchase order map group';
}

function ReceiveAllocatedLineItemModalBody({
    companySourceOptions,
    defaultPartnerLot,
    errorMessage,
    expirationDefaults,
    isSubmitting,
    isThirdPartyAsn,
    onReceive,
    suggestedAllocations,
}) {
    const [numPallets, setNumPallets] = useState('');
    const [validationMessage, setValidationMessage] = useState(null);
    const expirationSeed = useMemo(
        () => ({
            showExpiration: expirationDefaults.showExpiration === true,
            defaultDate: expirationDefaults.defaultDate ?? '',
        }),
        [expirationDefaults.defaultDate, expirationDefaults.showExpiration],
    );
    const lotSeed = useMemo(
        () => ({
            defaultLot: isThirdPartyAsn ? (defaultPartnerLot ?? '').trim() : '',
        }),
        [defaultPartnerLot, isThirdPartyAsn],
    );
    const reducer = useCallback(
        (state, action) => palletStagingReducer(state, action, expirationSeed, lotSeed),
        [expirationSeed, lotSeed],
    );
    const [pallets, dispatch] = useReducer(reducer, []);
    const resolvedNumPallets = Math.max(0, Math.floor(Number(numPallets) || 0));
    const showPalletBuilder = resolvedNumPallets > 0;

    const handleNumPalletsChange = useCallback(
        (event) => {
            const sanitized = sanitizeNumPalletsInput(event.target.value);
            setNumPallets(sanitized);
            setValidationMessage(null);
            dispatch({
                type: 'SYNC_NUM_PALLETS',
                numPallets: Math.max(0, Math.floor(Number(sanitized) || 0)),
                companySourceOptions,
                isThirdPartyAsn,
            });
        },
        [companySourceOptions, isThirdPartyAsn],
    );

    useEffect(() => {
        if (pallets.length === 0) {
            return;
        }

        dispatch({
            type: 'AUTO_FILL_SINGLE_SOURCE',
            companySourceOptions,
            isThirdPartyAsn,
        });
    }, [companySourceOptions, isThirdPartyAsn, pallets.length]);

    const handleApplyToAllPallets = useCallback(
        (values) => {
            dispatch({ type: 'APPLY_QUICK_FILL', values, isThirdPartyAsn });
        },
        [isThirdPartyAsn],
    );

    const handleSubmitAndPrintLabels = useCallback(() => {
        const caseQtyError = getCaseQtyValidationError(pallets);
        if (caseQtyError) {
            setValidationMessage(caseQtyError);
            toast.error(caseQtyError);
            return;
        }

        const expirationError = getExpirationValidationError(pallets, expirationDefaults);
        if (expirationError) {
            setValidationMessage(expirationError);
            toast.error(expirationError);
            return;
        }

        setValidationMessage(null);
        onReceive({ pallets, receiveQty0: false });
    }, [expirationDefaults, onReceive, pallets]);

    const handleReceiveZeroPallets = useCallback(() => {
        onReceive({ pallets: [], receiveQty0: true });
    }, [onReceive]);

    return (
        <div className={showPalletBuilder ? 'flex min-h-0 flex-1 flex-col' : undefined}>
            <div className={showPalletBuilder ? 'shrink-0' : undefined}>
                {validationMessage ? (
                    <Alert className="mb-4" variant="destructive">
                        <AlertTitle>Cannot submit</AlertTitle>
                        <AlertDescription>{validationMessage}</AlertDescription>
                    </Alert>
                ) : null}
                {errorMessage ? (
                    <Alert className="mb-4" variant="destructive">
                        <AlertTitle>Receive failed</AlertTitle>
                        <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>
                ) : null}
                <AllocationBreakdown allocations={suggestedAllocations} />
                <div className="mt-4 flex items-end justify-between gap-6">
                    <div className="w-40">
                        <Label htmlFor="num-pallets">Num Pallets</Label>
                        <Input
                            id="num-pallets"
                            className="mt-2"
                            disabled={isSubmitting}
                            min={0}
                            step={1}
                            type="number"
                            value={numPallets}
                            onChange={handleNumPalletsChange}
                        />
                    </div>
                    <Button disabled={isSubmitting} type="button" variant="outline" onClick={handleReceiveZeroPallets}>
                        Receive 0 Pallets
                    </Button>
                </div>
                {showPalletBuilder ? (
                    <PalletQuickFillCard
                        className="mt-4"
                        companySourceOptions={companySourceOptions}
                        expirationDefaults={expirationDefaults}
                        isThirdPartyAsn={isThirdPartyAsn}
                        onApplyToAllPallets={handleApplyToAllPallets}
                    />
                ) : null}
            </div>
            {showPalletBuilder ? (
                <>
                    <div className="mt-3 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
                        {pallets.map((pallet, index) => (
                            <PalletBuilderCard
                                key={pallet.id}
                                companySourceOptions={companySourceOptions}
                                expirationDefaults={expirationDefaults}
                                isThirdPartyAsn={isThirdPartyAsn}
                                pallet={pallet}
                                palletIndex={index}
                                onAddLine={() =>
                                    dispatch({
                                        type: 'ADD_LINE',
                                        palletId: pallet.id,
                                        companySourceOptions,
                                        isThirdPartyAsn,
                                    })
                                }
                                onRemoveLine={(lineId) =>
                                    dispatch({ type: 'REMOVE_LINE', palletId: pallet.id, lineId })
                                }
                                onUpdateLine={(lineId, patch) =>
                                    dispatch({ type: 'UPDATE_LINE', palletId: pallet.id, lineId, patch })
                                }
                            />
                        ))}
                    </div>
                    <ReceiveFooter
                        companySourceOptions={companySourceOptions}
                        isThirdPartyAsn={isThirdPartyAsn}
                        isSubmitting={isSubmitting}
                        pallets={pallets}
                        suggestedAllocations={suggestedAllocations}
                        onSubmit={handleSubmitAndPrintLabels}
                    />
                </>
            ) : null}
        </div>
    );
}

function ReceiveAllocatedLineItemModal({ close, isThirdPartyAsn = false, open, poType, rowData }) {
    const lineItemKey = getLineItemKey(rowData);
    const [showPrintLabels, setShowPrintLabels] = useState(false);
    const [receivedPrintData, setReceivedPrintData] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [receiveWarnings, setReceiveWarnings] = useState([]);

    const { data: companySources = [] } = useCompanySourceOptionsQuery({ enabled: open });

    const purchaseOrderMaps = useMemo(() => getPurchaseOrderMapsFromRow(rowData, poType), [poType, rowData]);

    const suggestedAllocations = useMemo(
        () => buildSuggestedAllocations(purchaseOrderMaps, companySources),
        [companySources, purchaseOrderMaps],
    );

    const companySourceOptions = useMemo(
        () => buildCompanySourceOptionsFromMaps(purchaseOrderMaps, companySources),
        [companySources, purchaseOrderMaps],
    );

    const expirationDefaults = useMemo(() => resolveExpirationDefaults(rowData, poType), [poType, rowData]);

    const defaultPartnerLot = rowData?.original?.partnerLotNumber ?? null;

    const { isPending: isSubmitting, mutateAsync } = useReceivePurchaseOrderMapGroupMutation();

    const handleOpenChange = useCallback(
        (isOpen) => {
            if (!isOpen) {
                setShowPrintLabels(false);
                setReceivedPrintData(null);
                setErrorMessage(null);
                setReceiveWarnings([]);
                close();
            }
        },
        [close],
    );

    const handleSubmitSuccess = useCallback((printData, warnings = []) => {
        setReceivedPrintData(printData);
        setReceiveWarnings(warnings);
        setShowPrintLabels(true);
    }, []);

    const handleReceive = useCallback(
        async ({ pallets, receiveQty0 }) => {
            if (lineItemKey == null) {
                setErrorMessage('Missing purchase order map group ID');
                return;
            }

            setErrorMessage(null);
            setReceiveWarnings([]);

            try {
                const payload = buildPurchaseOrderMapGroupReceivePayload({
                    poType,
                    pallets,
                    purchaseOrderMaps,
                    receiveQty0,
                    isThirdPartyAsn,
                });

                const data = await mutateAsync({
                    poType,
                    groupId: lineItemKey,
                    payload,
                });

                const warnings = (data.warnings ?? []).map((warning) => warning.msg).filter(Boolean);
                const printData = buildReceivedPrintDataFromApiResponse({
                    response: data,
                    poType,
                    purchaseOrderMaps,
                    companySourceOptions,
                });

                if (warnings.length > 0) {
                    toast.warning(`Purchase order line item received, with warnings: ${warnings.join('; ')}`);
                } else {
                    toast.success('Purchase order line item received');
                }

                handleSubmitSuccess(printData, warnings);
            } catch (error) {
                const message = getReceiveErrorMessage(error);
                setErrorMessage(message);
                toast.error(message);
            }
        },
        [
            companySourceOptions,
            handleSubmitSuccess,
            isThirdPartyAsn,
            lineItemKey,
            mutateAsync,
            poType,
            purchaseOrderMaps,
        ],
    );

    useEffect(() => {
        if (!open) {
            setShowPrintLabels(false);
            setReceivedPrintData(null);
            setErrorMessage(null);
            setReceiveWarnings([]);
        }
    }, [open]);

    if (!rowData) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                aria-describedby={undefined}
                className="flex max-h-[90vh] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
            >
                {showPrintLabels && receivedPrintData ? (
                    <PrintAllocatedLabelsModal
                        isThirdPartyAsn={isThirdPartyAsn}
                        partnerDisplayName={null}
                        poType={poType}
                        receiveWarnings={receiveWarnings}
                        receivedPrintData={receivedPrintData}
                        rowData={rowData}
                    />
                ) : (
                    <>
                        <div className="shrink-0 rounded-t-lg border-2 border-[#4C884B] bg-[#B0C472] px-6 py-4">
                            <DialogTitle className="p-0 text-base font-semibold">
                                {getHeaderText(rowData, poType)}
                            </DialogTitle>
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
                            <ReceiveAllocatedLineItemModalBody
                                key={`${lineItemKey}-${open}`}
                                companySourceOptions={companySourceOptions}
                                defaultPartnerLot={defaultPartnerLot}
                                errorMessage={errorMessage}
                                expirationDefaults={expirationDefaults}
                                isThirdPartyAsn={isThirdPartyAsn}
                                isSubmitting={isSubmitting}
                                suggestedAllocations={suggestedAllocations}
                                onReceive={handleReceive}
                            />
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export { ReceiveAllocatedLineItemModal, getHeaderText, getLineItemKey, getPurchaseOrderMapsFromRow };
